import React, { useState, useEffect, useRef } from 'react';
import { 
  Paper, 
  Typography, 
  Grid, 
  FormControlLabel, 
  Switch, 
  Button, 
  styled,
  Divider,
  Box,
  Alert,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  ToggleButtonGroup,
  ToggleButton,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import DeleteIcon from '@mui/icons-material/Delete';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SmartButtonIcon from '@mui/icons-material/SmartButton';
import SlideSpecificSettingsModal from './SlideSpecificSettingsModal';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal';
import TextColorPalette from './shared/TextColorPalette';
import { renderAccentSplitTitle } from '../../../utils/accentSplitTitle';
import TrustStripSettingsPanel, {
  DEFAULT_TRUST_STRIP,
  normalizeTrustStripSettings,
} from './shared/TrustStripSettingsPanel';
import type { TrustStripSettings } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import { packSlideButtonStyle } from './slideButtonTemplates';

import { 
  getSliderSettings, 
  saveSliderSettings, 
  addSlide, 
  updateSlide, 
  deleteSlide,
  toggleSlide 
} from '../../../services/ecommerceApi';

// ECommercePage.tsx'den taşınan stil
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
  }
}));

// Tipler
interface SliderTextStyle {
  text: string;
  color: string;
  fontSize: string;
  fontFamily: string;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  titleColorMode?: 'solid' | 'accent-split';
  highlightText?: string;
  highlightFontSize?: string;
  highlightTextColor?: string;
}

interface SliderBackgroundSettings {
  type: 'solid' | 'linear';
  color?: string;
  colors?: string[];
  angle?: number;
}

type SlideButtonAction = 'scroll-to-section' | 'external-url' | 'contact-page' | 'products-page' | 'open-form' | 'none';

interface SlideButton {
  show?: boolean;
  label?: string;
  action?: SlideButtonAction;
  target?: string;
  style?: 'primary' | 'secondary' | 'outline' | 'custom';
  formSlug?: string;
  formTitle?: string;
  templateId?: string;
  size?: 'sm' | 'md' | 'lg';
  placement?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  borderRadius?: 'md' | 'full';
}

interface SlideItem {
  id: string;
  mediaUrl: string;
  mediaType: 'image' | 'video';
  title: string;
  active: boolean;
  mediaPath?: string;
  overrideTitle?: SliderTextStyle;
  overrideDescription?: SliderTextStyle;
  overrideTextPosition?: string;
  overrideTextAlign?: SliderTextAlign;
  overrideTextAnimation?: string;
  overlayArea?: 'full' | 'left' | 'center' | 'right';
  mobileMediaUrl?: string;
  mobileMediaPath?: string;
  buttons?: SlideButton[];
  button?: SlideButton;
}

const getSlideButtons = (slide: SlideItem): SlideButton[] => {
  if (Array.isArray(slide.buttons) && slide.buttons.length > 0) return slide.buttons;
  if (slide.button) return [slide.button];
  return [];
};

type SliderTextMode = 'single' | 'per-slide' | 'fixed';
type SliderTextAlign = 'left' | 'center' | 'right';

interface SliderBottomDarken {
  enabled: boolean;
  intensity: number;
}

interface SliderImageBlur {
  enabled: boolean;
  amount: number;
}

type SliderTextInsetMode = 'edge' | 'grid' | 'custom';

interface SliderTextInset {
  mode: SliderTextInsetMode;
  customPx?: number;
  offsetX?: number;
}

const DEFAULT_BOTTOM_DARKEN: SliderBottomDarken = { enabled: false, intensity: 50 };
const DEFAULT_IMAGE_BLUR: SliderImageBlur = { enabled: false, amount: 8 };
const DEFAULT_TEXT_INSET: SliderTextInset = { mode: 'grid', customPx: 48, offsetX: 0 };

const normalizeBottomDarken = (raw: unknown): SliderBottomDarken => {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_BOTTOM_DARKEN };
  const bag = raw as { enabled?: unknown; intensity?: unknown };
  const intensity = Number(bag.intensity);
  return {
    enabled: Boolean(bag.enabled),
    intensity: Number.isFinite(intensity) ? Math.min(80, Math.max(0, Math.round(intensity))) : 50,
  };
};

const normalizeImageBlur = (raw: unknown): SliderImageBlur => {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_IMAGE_BLUR };
  const bag = raw as { enabled?: unknown; amount?: unknown };
  const amount = Number(bag.amount);
  return {
    enabled: Boolean(bag.enabled),
    amount: Number.isFinite(amount) ? Math.min(20, Math.max(0, Math.round(amount))) : 8,
  };
};

const normalizeTextInset = (raw: unknown): SliderTextInset => {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_TEXT_INSET };
  const bag = raw as { mode?: unknown; customPx?: unknown; offsetX?: unknown };
  const mode = bag.mode === 'edge' || bag.mode === 'custom' ? bag.mode : 'grid';
  const customPx = Number(bag.customPx);
  const offsetX = Number(bag.offsetX);
  return {
    mode,
    customPx: Number.isFinite(customPx) ? Math.min(200, Math.max(8, Math.round(customPx))) : 48,
    offsetX: Number.isFinite(offsetX) ? Math.min(160, Math.max(-160, Math.round(offsetX))) : 0,
  };
};

const resolveTextAlign = (explicit?: string, position?: string): SliderTextAlign => {
  if (explicit === 'left' || explicit === 'center' || explicit === 'right') return explicit;
  const pos = position || 'middle-center';
  if (pos.includes('left')) return 'left';
  if (pos.includes('right')) return 'right';
  return 'center';
};

interface SliderSettings {
    enabled: boolean;
    autoplay: boolean;
    interval: number;
    slides: SlideItem[];
    title?: SliderTextStyle;
    description?: SliderTextStyle;
    background?: SliderBackgroundSettings;
    textPosition?: string;
    textAlign?: SliderTextAlign;
    textAnimation?: string;
    textMode?: SliderTextMode;
    textSlideId?: string;
    bottomDarken?: SliderBottomDarken;
    imageBlur?: SliderImageBlur;
    textInset?: SliderTextInset;
    showTrustStrip?: boolean;
    trustStripSettings?: TrustStripSettings;
    height?: {
        web: number;
        mobile: number;
    } | number;
}

interface SliderSettingsProps {
  settings: SliderSettings;
  onSettingsChange: (setting: keyof SliderSettings, value: any) => void;
  companyId: string | null;
  configId: string | null; // ✅ YENİ: SQL için configId
}

const SliderSettingsComponent: React.FC<SliderSettingsProps> = ({ 
  settings, 
  onSettingsChange, 
  companyId,
  configId // ✅ YENİ
}) => {
  const { t } = useTranslation('salesSystem');
  const [isImageLibraryOpen, setIsImageLibraryOpen] = useState(false);
  const [isMobileImageLibraryOpen, setIsMobileImageLibraryOpen] = useState(false);
  const [mobileImageTargetSlideId, setMobileImageTargetSlideId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingSlide, setEditingSlide] = useState<SlideItem | null>(null);
  const [slideModalFocus, setSlideModalFocus] = useState<'general' | 'button'>('general');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [slideToDelete, setSlideToDelete] = useState<SlideItem | null>(null);
  const [sliderId, setSliderId] = useState<string | null>(null);
  const [trustStripSaving, setTrustStripSaving] = useState(false);
  const [trustStripSaved, setTrustStripSaved] = useState(false);
  const [trustStripError, setTrustStripError] = useState<string | null>(null);

  // Auto-save için debounce timer
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const settingsRef = useRef(settings);
  const pendingSaveRef = useRef<{ setting: keyof SliderSettings; value: any } | null>(null);

  settingsRef.current = settings;

  const buildSliderSavePayload = (setting: keyof SliderSettings, value: any, latest: SliderSettings) => {
    const titleSettingsJson = setting === 'title'
      ? JSON.stringify(value)
      : (latest.title ? JSON.stringify(latest.title) : null);

    const descriptionSettingsJson = setting === 'description'
      ? JSON.stringify(value)
      : (latest.description ? JSON.stringify(latest.description) : null);

    const textMode =
      setting === 'textMode' ? value : (latest.textMode || 'per-slide');
    const textSlideId =
      setting === 'textSlideId' ? value : (latest.textSlideId || null);
    const baseBackground =
      setting === 'background'
        ? value
        : (latest.background || { type: 'solid', color: '#2e3141' });
    const trustEnabled =
      setting === 'showTrustStrip' ? Boolean(value) : Boolean(latest.showTrustStrip);
    const trustSettings =
      setting === 'trustStripSettings'
        ? normalizeTrustStripSettings(value)
        : normalizeTrustStripSettings(latest.trustStripSettings || DEFAULT_TRUST_STRIP);
    const textPosition = setting === 'textPosition'
      ? value
      : (latest.textPosition || 'middle-center');
    const textAlign =
      setting === 'textAlign'
        ? value
        : resolveTextAlign(latest.textAlign, textPosition);
    const bottomDarken =
      setting === 'bottomDarken'
        ? normalizeBottomDarken(value)
        : normalizeBottomDarken(latest.bottomDarken);
    const imageBlur =
      setting === 'imageBlur'
        ? normalizeImageBlur(value)
        : normalizeImageBlur(latest.imageBlur);
    const textInset =
      setting === 'textInset'
        ? normalizeTextInset(value)
        : normalizeTextInset(latest.textInset);

    let heightValue: number;
    let mobileHeightValue: number;
    if (setting === 'height') {
      heightValue = typeof value === 'object' ? Number(value.web) : Number(value);
      mobileHeightValue = typeof value === 'object' ? Number(value.mobile) : 40;
    } else {
      heightValue = typeof latest.height === 'object' ? Number(latest.height.web || 60) : Number(latest.height || 60);
      mobileHeightValue = typeof latest.height === 'object' ? Number(latest.height.mobile || 40) : 40;
    }
    if (!Number.isFinite(heightValue) || heightValue <= 0) heightValue = 60;
    if (!Number.isFinite(mobileHeightValue) || mobileHeightValue <= 0) mobileHeightValue = 40;

    const backgroundSettingsJson = JSON.stringify({
      ...baseBackground,
      textOverlayMode: textMode,
      textOverlaySlideId: textSlideId,
      textAlign,
      textPosition,
      bottomDarken,
      imageBlur,
      textInset,
      heightWeb: heightValue,
      heightMobile: mobileHeightValue,
      trustStrip: {
        enabled: trustEnabled,
        ...trustSettings,
      },
    });

    const textAnimation = setting === 'textAnimation'
      ? value
      : (latest.textAnimation || 'fade-in');

    return {
      showSlider: setting === 'enabled' ? value : latest.enabled,
      height: heightValue,
      mobileHeight: mobileHeightValue,
      autoPlay: setting === 'autoplay' ? value : latest.autoplay,
      autoPlayInterval: setting === 'interval' ? value : latest.interval,
      titleSettingsJson,
      descriptionSettingsJson,
      backgroundSettingsJson,
      textPosition,
      textAnimation,
    };
  };

  // ConfigId geldiğinde Slider'ı SQL'den yükle ve sliderId'yi al
  useEffect(() => {
    const loadSliderFromSQL = async () => {
      if (configId && companyId) {
        console.log('🎠 [SLIDER LOAD] ConfigId geldi, Slider yükleniyor:', { configId, companyId });
        try {
          const response = await getSliderSettings(companyId, configId);
          console.log('📥 [SLIDER LOAD] SQL\'den gelen yanıt:', JSON.stringify(response, null, 2));
          
          if (response && response.success && response.data && response.data.slider) {
            setSliderId(response.data.slider.id);
            console.log('✅ [SLIDER LOAD] SliderId set edildi:', response.data.slider.id);
          } else {
            console.warn('⚠️ [SLIDER LOAD] SQL\'de slider kaydı bulunamadı - ilk görsel eklendiğinde otomatik oluşturulacak');
          }
        } catch (error) {
          console.error('❌ [SLIDER LOAD] Slider yükleme hatası:', error);
        }
      } else {
        console.warn('⚠️ [SLIDER LOAD] configId veya companyId henüz yok:', { configId, companyId });
      }
    };

    loadSliderFromSQL();
  }, [configId, companyId]);

  const positionOptions = {
    'middle-center': t('ecommerceSlideModal.positionOptions.middle-center'),
    'top-center': t('ecommerceSlideModal.positionOptions.top-center'),
    'bottom-center': t('ecommerceSlideModal.positionOptions.bottom-center'),
    'middle-left': t('ecommerceSlideModal.positionOptions.middle-left'),
    'middle-right': t('ecommerceSlideModal.positionOptions.middle-right'),
    'top-left': t('ecommerceSlideModal.positionOptions.top-left'),
    'top-right': t('ecommerceSlideModal.positionOptions.top-right'),
    'bottom-left': t('ecommerceSlideModal.positionOptions.bottom-left'),
    'bottom-right': t('ecommerceSlideModal.positionOptions.bottom-right'),
  };

  const positionGrid: Array<Array<{ value: string; label: string }>> = [
    [
      { value: 'top-left', label: '↖' },
      { value: 'top-center', label: '↑' },
      { value: 'top-right', label: '↗' },
    ],
    [
      { value: 'middle-left', label: '←' },
      { value: 'middle-center', label: '●' },
      { value: 'middle-right', label: '→' },
    ],
    [
      { value: 'bottom-left', label: '↙' },
      { value: 'bottom-center', label: '↓' },
      { value: 'bottom-right', label: '↘' },
    ],
  ];

  const animationOptions = {
    'none': t('ecommerceSlideModal.animationOptions.none'),
    'fade-in': t('ecommerceSlideModal.animationOptions.fade-in'),
    'slide-in-from-bottom': t('ecommerceSlideModal.animationOptions.slide-in-from-bottom'),
    'slide-in-from-top': t('ecommerceSlideModal.animationOptions.slide-in-from-top'),
    'slide-in-from-left': t('ecommerceSlideModal.animationOptions.slide-in-from-left'),
    'slide-in-from-right': t('ecommerceSlideModal.animationOptions.slide-in-from-right'),
    'zoom-in': t('ecommerceSlideModal.animationOptions.zoom-in'),
    'word-mask-up': t('ecommerceSlideModal.animationOptions.word-mask-up'),
    'word-fade-up': t('ecommerceSlideModal.animationOptions.word-fade-up'),
    'line-mask-up': t('ecommerceSlideModal.animationOptions.line-mask-up'),
  };

  const fontOptions = [
    "Arial, sans-serif",
    "Verdana, sans-serif",
    "Georgia, serif",
    "'Times New Roman', Times, serif",
    "'Courier New', Courier, monospace",
    "Impact, sans-serif",
    "'Comic Sans MS', cursive, sans-serif",
  ];

  const defaultTitleStyle: SliderTextStyle = {
    text: '',
    color: '#FFFFFF',
    fontSize: '48px',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    fontStyle: 'normal',
  };

  const defaultDescriptionStyle: SliderTextStyle = {
    text: '',
    color: '#F0F0F0',
    fontSize: '18px',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
    fontStyle: 'normal',
  };

  const titleSettings = { ...defaultTitleStyle, ...settings.title };
  const descriptionSettings = { ...defaultDescriptionStyle, ...settings.description };

  const defaultBackgroundSettings: SliderBackgroundSettings = {
    type: 'solid',
    color: '#2e3141',
    colors: ['#4e54c8', '#8f94fb'],
    angle: 90,
  };

  const backgroundSettings = { ...defaultBackgroundSettings, ...settings.background };

  const handleOpenImageLibrary = () => setIsImageLibraryOpen(true);
  const handleCloseImageLibrary = () => setIsImageLibraryOpen(false);

  const handleOpenSlideSettingsModal = (
    slide: SlideItem,
    focus: 'general' | 'button' = 'general'
  ) => {
    setSlideModalFocus(focus);
    setEditingSlide(slide);
  };
  const handleCloseSlideSettingsModal = () => {
    setEditingSlide(null);
    setSlideModalFocus('general');
  };

  const parseFontSizePx = (value?: string): number | undefined => {
    const n = parseInt(String(value || '').replace(/[^\d]/g, ''), 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };

  const handleSaveSlideSettings = async (updatedSlide: SlideItem) => {
    if (!companyId || !sliderId) {
      setError("Şirket ID veya Slider ID bulunamadı. Kayıt yapılamadı.");
      throw new Error('Missing companyId or sliderId');
    }

    const updatedSlides = settings.slides.map(s => s.id === updatedSlide.id ? updatedSlide : s);
    const overlayTitle = updatedSlide.overrideTitle;
    const overlayDescription = updatedSlide.overrideDescription;
    const buttons = getSlideButtons(updatedSlide).map((b) => ({
      show: Boolean(b.show),
      label: (b.label || '').trim(),
      action: b.action || 'none',
      target: b.action === 'open-form' ? (b.formSlug || b.target || '').trim() : (b.target || '').trim(),
      style: b.style || 'primary',
      formSlug: (b.formSlug || '').trim() || undefined,
      formTitle: (b.formTitle || '').trim() || undefined,
      templateId: b.templateId || undefined,
      size: b.size || 'md',
      placement: b.placement || 'with-text',
      backgroundColor: b.backgroundColor || undefined,
      textColor: b.textColor || undefined,
      borderColor: b.borderColor || undefined,
      borderRadius: b.borderRadius || 'md',
    }));
    const primary = buttons.find((b) => b.show && b.label) || buttons[0];
    const overlayMeta = {
      overrideTitle: overlayTitle || null,
      overrideDescription: overlayDescription || null,
      overrideTextPosition: updatedSlide.overrideTextPosition || 'middle-center',
      overrideTextAlign: updatedSlide.overrideTextAlign || resolveTextAlign(settings.textAlign, updatedSlide.overrideTextPosition),
      overrideTextAnimation: updatedSlide.overrideTextAnimation || 'fade-in',
      overlayArea: updatedSlide.overlayArea || 'full',
      buttons,
      // Geriye dönük uyumluluk
      button: primary || null,
    };

    try {
      // stil+aksiyon+şablon+boyut+renk (max 50): overlay JSON kesilse bile vitrin okur
      const buttonStylePacked = primary
        ? packSlideButtonStyle(primary)
        : 'primary|none';
      await updateSlide({
        companyId,
        sliderId,
        slideId: updatedSlide.id,
        ...(updatedSlide.mediaUrl ? { imageUrl: updatedSlide.mediaUrl } : {}),
        mobileImageUrl: updatedSlide.mobileMediaUrl || '',
        title: overlayTitle?.text || updatedSlide.title || '',
        description: overlayDescription?.text || '',
        subtitle: JSON.stringify(overlayMeta),
        textPosition: updatedSlide.overrideTextPosition || 'middle-center',
        textAlignment: updatedSlide.overrideTextAlign || resolveTextAlign(settings.textAlign, updatedSlide.overrideTextPosition),
        titleColor: overlayTitle?.color || '#FFFFFF',
        descriptionColor: overlayDescription?.color || '#FFFFFF',
        titleFontSize: parseFontSizePx(overlayTitle?.fontSize) ?? 48,
        descriptionFontSize: parseFontSizePx(overlayDescription?.fontSize) ?? 18,
        showButton: Boolean(primary?.show && primary?.label),
        buttonText: (primary?.label || '').trim(),
        buttonUrl: (primary?.target || '').trim(),
        buttonStyle: buttonStylePacked,
      });

      onSettingsChange('slides', updatedSlides);
      notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
      handleCloseSlideSettingsModal();
    } catch (err) {
      console.error("Özel slayt ayarları kaydedilirken hata:", err);
      setError("Slayt ayarları kaydedilirken bir hata oluştu.");
      throw err;
    }
  };

  const generateCssBackground = (bg: SliderBackgroundSettings): string => {
    if (bg.type === 'solid') {
      return bg.color || '#ffffff';
    }
    if (bg.type === 'linear') {
      const colors = (bg.colors && bg.colors.length > 0) ? bg.colors.join(', ') : 'transparent';
      return `linear-gradient(${bg.angle || 90}deg, ${colors})`;
    }
    return 'transparent';
  };

  const handleImageLibrarySave = async (selectedUrls: string[]) => {
    console.log('🖼️ [SLIDER] handleImageLibrarySave çağrıldı:', {
      companyId,
      sliderId,
      configId,
      selectedUrls,
      urlCount: selectedUrls.length
    });

    if (!companyId || selectedUrls.length === 0) {
      console.warn('⚠️ [SLIDER] companyId veya seçilen URL yok, çıkılıyor');
      return;
    }

    setError(null);
    let currentSliderId = sliderId;

    try {
      // sliderId yoksa önce slider kaydını oluştur
      if (!currentSliderId) {
        console.log('🔧 [SLIDER] sliderId yok, önce slider oluşturuluyor...');
        if (!configId) {
          console.error('❌ [SLIDER] configId de yok, slider oluşturulamaz');
          setError('E-ticaret yapılandırma bilgisi bulunamadı. Lütfen sayfayı yenileyip tekrar deneyin.');
          return;
        }

        const saveResponse = await saveSliderSettings({
          configId,
          companyId,
          showSlider: settings.enabled,
          autoPlay: settings.autoplay,
          autoPlayInterval: settings.interval,
          height: typeof settings.height === 'object' ? settings.height.web : (settings.height || 60),
          mobileHeight: typeof settings.height === 'object' ? settings.height.mobile : 40,
        });

        console.log('📥 [SLIDER] saveSliderSettings yanıtı:', saveResponse);

        if (saveResponse?.success && saveResponse?.data?.id) {
          currentSliderId = saveResponse.data.id;
          setSliderId(currentSliderId);
          console.log('✅ [SLIDER] Yeni slider oluşturuldu, sliderId:', currentSliderId);
        } else {
          console.error('❌ [SLIDER] Slider oluşturulamadı:', saveResponse);
          setError('Slider kaydı oluşturulamadı. Lütfen tekrar deneyin.');
          return;
        }
      }

      console.log('➕ [SLIDER] Slide\'lar ekleniyor, sliderId:', currentSliderId);
      const addedSlides: SlideItem[] = [];
      for (const imageUrl of selectedUrls) {
        console.log('  ➕ Slide ekleniyor:', imageUrl.substring(0, 80) + '...');
        const addResponse = await addSlide({
          sliderId: currentSliderId,
          companyId,
          imageUrl,
          title: '',
          displayOrder: settings.slides.length + addedSlides.length,
        });
        const newId = addResponse?.data?.id || addResponse?.data?.Id;
        addedSlides.push({
          id: String(newId || `tmp_${Date.now()}_${addedSlides.length}`),
          mediaUrl: imageUrl,
          mediaType: 'image',
          title: '',
          active: true,
        });
      }
      console.log('✅ [SLIDER] Tüm slide\'lar eklendi');

      onSettingsChange('slides', [...(settings.slides || []), ...addedSlides]);

      handleCloseImageLibrary();
    } catch (err) {
      console.error("❌ [SLIDER] Slider görseli eklenirken hata:", err);
      setError("Görsel eklenirken bir hata oluştu.");
    }
  };

  const handleInitiateDelete = (slide: SlideItem) => {
    setSlideToDelete(slide);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setSlideToDelete(null);
  };

  const handleDeleteSlide = async () => {
    if (!companyId || !slideToDelete || !slideToDelete.id || !sliderId) {
        setError("Gerekli bilgiler eksik, silme işlemi yapılamadı.");
        handleCloseDeleteConfirm();
        return;
    }
    setIsDeleting(slideToDelete.id);
    setError(null);
    try {
        try {
          await deleteSlide({
            companyId,
            sliderId,
            slideId: slideToDelete.id,
          });
        } catch (deleteErr: any) {
          const status = deleteErr?.response?.status;
          if (status !== 404 && status !== 405) throw deleteErr;
          // Eski API: kalıcı silme yoksa pasife al
          await toggleSlide({
            companyId,
            sliderId,
            slideId: slideToDelete.id,
            isActive: false,
          });
        }

        const updatedSlides = settings.slides.filter(slide => slide.id !== slideToDelete.id);
        onSettingsChange('slides', updatedSlides);

    } catch (err) {
        console.error("Slider medyası silinirken hata:", err);
        setError("Medya silinirken bir hata oluştu.");
    } finally {
        setIsDeleting(null);
        handleCloseDeleteConfirm();
    }
  };

  const handleOpenMobileImageLibrary = (slideId: string) => {
    setMobileImageTargetSlideId(slideId);
    setIsMobileImageLibraryOpen(true);
  };

  const handleMobileImageLibrarySave = async (selectedUrls: string[]) => {
    if (!companyId || !sliderId || !mobileImageTargetSlideId || selectedUrls.length === 0) return;

    setError(null);
    try {
      const downloadURL = selectedUrls[0];

      const targetSlide = settings.slides.find((s) => s.id === mobileImageTargetSlideId);
      await updateSlide({
        companyId,
        sliderId,
        slideId: mobileImageTargetSlideId,
        imageUrl: targetSlide?.mediaUrl,
        mobileImageUrl: downloadURL,
      });

      const updatedSlides = settings.slides.map(slide => {
        if (slide.id === mobileImageTargetSlideId) {
          return { ...slide, mobileMediaUrl: downloadURL };
        }
        return slide;
      });

      onSettingsChange('slides', updatedSlides);
      setIsMobileImageLibraryOpen(false);
      setMobileImageTargetSlideId(null);
    } catch (err) {
      console.error("Mobil slider resmi eklenirken hata:", err);
      setError("Mobil resim eklenirken bir hata oluştu.");
    }
  };

  const handleRemoveMobileImage = async (slideId: string) => {
    if (!companyId || !sliderId) {
      setError("Şirket ID veya Slider ID bulunamadı.");
      return;
    }

    const slide = settings.slides.find(s => s.id === slideId);
    if (!slide) return;

    try {
      await updateSlide({
        companyId,
        sliderId,
        slideId,
        imageUrl: slide.mediaUrl,
        mobileImageUrl: '',
      });

      const updatedSlides = settings.slides.map(s => {
        if (s.id === slideId) {
          const { mobileMediaUrl, mobileMediaPath, ...rest } = s;
          return rest;
        }
        return s;
      });

      onSettingsChange('slides', updatedSlides);

    } catch (err) {
      console.error("Mobil slider resmi silinirken hata:", err);
      setError("Mobil resim silinirken bir hata oluştu.");
    }
  };

  // ✅ Auto-save fonksiyonu
  const persistSliderSettings = async (setting: keyof SliderSettings, value: any) => {
    if (!companyId || !configId) {
      pendingSaveRef.current = { setting, value };
      console.warn('⚠️ [SLIDER AUTO-SAVE] CompanyId veya ConfigId yok, kayıt kuyruğa alındı');
      return;
    }

    const latest = {
      ...settingsRef.current,
      [setting]: value,
    };
    const sliderData = {
      configId,
      companyId,
      ...buildSliderSavePayload(setting, value, latest),
    };

    try {
      console.log('💾 [SLIDER AUTO-SAVE] Kaydediliyor:', { setting, height: sliderData.height });
      await saveSliderSettings(sliderData);
      pendingSaveRef.current = null;
      console.log('✅ [SLIDER AUTO-SAVE] Başarılı');
      notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
    } catch (error) {
      console.error('❌ [SLIDER AUTO-SAVE] Hata:', error);
      setError('Slider ayarları kaydedilirken bir hata oluştu.');
    }
  };

  const autoSaveSliderSettings = (setting: keyof SliderSettings, value: any) => {
    pendingSaveRef.current = { setting, value };
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      void persistSliderSettings(setting, value);
    }, 400);
  };

  useEffect(() => {
    if (!companyId || !configId || !pendingSaveRef.current) return;
    const pending = pendingSaveRef.current;
    void persistSliderSettings(pending.setting, pending.value);
  }, [companyId, configId]);

  const handleSettingChange = async (setting: keyof SliderSettings, value: any) => {
    if (!companyId) {
      setError("Şirket ID bulunamadı. Ayar değiştirilemedi.");
      return;
    }
    
    // Parent state'i anlık güncelle
    onSettingsChange(setting, value);

    // ✅ Auto-save
    autoSaveSliderSettings(setting, value);
  };

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>{t('ecommerceSliderSettings.sqlInfoTitle')}</strong> {t('ecommerceSliderSettings.sqlInfoDescription')}
        </Typography>
      </Alert>

      <Paper elevation={3} sx={{ p: 3 }}>
        <SectionTitle variant="h6">
          <ViewCarouselIcon /> {t('ecommerceSliderSettings.title')}
        </SectionTitle>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={(e) => handleSettingChange('enabled', e.target.checked)}
                />
              }
              label={t('ecommerceSliderSettings.fields.sliderEnabled')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoplay}
                  onChange={(e) => handleSettingChange('autoplay', e.target.checked)}
                  disabled={!settings.enabled}
                />
              }
              label={t('ecommerceSliderSettings.fields.autoplay')}
            />
          </Grid>
          
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('ecommerceSliderSettings.fields.displayDuration')}
            </Typography>
            <Box sx={{ px: 1 }}>
              <TextField
                type="number"
                label={t('ecommerceSliderSettings.fields.duration')}
                value={settings.interval === 0 ? '' : settings.interval / 1000}
                onChange={(e) => {
                  const seconds = e.target.value;
                  if (seconds === '') {
                    handleSettingChange('interval', 0);
                  } else {
                    const newInterval = parseFloat(seconds) * 1000;
                    if (!isNaN(newInterval) && newInterval >= 0) {
                      handleSettingChange('interval', newInterval);
                    }
                  }
                }}
                onBlur={() => {
                  if (settings.interval <= 0) {
                    handleSettingChange('interval', 5000);
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('ecommerceSliderSettings.units.secondsShort')}</InputAdornment>,
                  inputProps: { min: 0.1, step: 0.1 }
                }}
                variant="outlined"
                size="small"
                disabled={!settings.autoplay || !settings.enabled}
                sx={{ maxWidth: 150 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
              {t('ecommerceSliderSettings.fields.displayDurationHelper')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('ecommerceSliderSettings.fields.sliderHeight')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {t('ecommerceSliderSettings.fields.sliderHeightHelper')}
            </Typography>
            
            {/* Web Yüksekliği */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('ecommerceSliderSettings.fields.webHeight')}
              </Typography>
              <Box sx={{ px: 1 }}>
                {(() => {
                  const webHeight =
                    typeof settings.height === 'object'
                      ? settings.height?.web || 60
                      : settings.height || 60;
                  const applyWebHeight = (vh: number) => {
                    const currentHeight =
                      typeof settings.height === 'object'
                        ? settings.height
                        : { web: settings.height || 60, mobile: 40 };
                    handleSettingChange('height', { ...currentHeight, web: vh });
                  };
                  return (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {webHeight}vh
                      </Typography>
                      <Slider
                        value={Math.min(140, Math.max(30, webHeight))}
                        onChange={(_e, newValue) => applyWebHeight(newValue as number)}
                        aria-labelledby="web-height-slider"
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}vh`}
                        step={5}
                        min={30}
                        max={140}
                        marks={[
                          { value: 30, label: '30vh' },
                          { value: 60, label: '60vh' },
                          { value: 80, label: '80vh' },
                          { value: 100, label: '100vh' },
                          { value: 140, label: '140vh' }
                        ]}
                        disabled={!settings.enabled}
                        sx={{ mt: 2 }}
                      />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                        {[30, 60, 80, 100, 140].map((vh) => (
                          <Button
                            key={vh}
                            size="small"
                            variant={webHeight === vh ? 'contained' : 'outlined'}
                            disabled={!settings.enabled}
                            onClick={() => applyWebHeight(vh)}
                            sx={{ textTransform: 'none', minWidth: 64 }}
                          >
                            {vh}vh
                          </Button>
                        ))}
                      </Box>
                    </>
                  );
                })()}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
                {t('ecommerceSliderSettings.fields.webHeightHelper')}
              </Typography>
            </Box>

            {/* Mobil Yüksekliği */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('ecommerceSliderSettings.fields.mobileHeight')}
              </Typography>
              <Box sx={{ px: 1 }}>
                {(() => {
                  const mobileHeight =
                    typeof settings.height === 'object'
                      ? settings.height?.mobile || 40
                      : 40;
                  const applyMobileHeight = (vh: number) => {
                    const currentHeight =
                      typeof settings.height === 'object'
                        ? settings.height
                        : { web: settings.height || 60, mobile: 40 };
                    handleSettingChange('height', { ...currentHeight, mobile: vh });
                  };
                  return (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {mobileHeight}vh
                      </Typography>
                      <Slider
                        value={Math.min(80, Math.max(25, mobileHeight))}
                        onChange={(_e, newValue) => applyMobileHeight(newValue as number)}
                        aria-labelledby="mobile-height-slider"
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}vh`}
                        step={5}
                        min={25}
                        max={80}
                        marks={[
                          { value: 25, label: '25vh' },
                          { value: 40, label: '40vh' },
                          { value: 80, label: '80vh' }
                        ]}
                        disabled={!settings.enabled}
                        sx={{ mt: 2 }}
                      />
                    </>
                  );
                })()}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
                {t('ecommerceSliderSettings.fields.mobileHeightHelper')}
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, height: '100%', opacity: settings.enabled ? 1 : 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={normalizeBottomDarken(settings.bottomDarken).enabled}
                        disabled={!settings.enabled}
                        onChange={(e) =>
                          handleSettingChange('bottomDarken', {
                            ...normalizeBottomDarken(settings.bottomDarken),
                            enabled: e.target.checked,
                          })
                        }
                      />
                    }
                    label={t('ecommerceSliderSettings.fields.bottomDarken')}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {t('ecommerceSliderSettings.fields.bottomDarkenHint')}
                  </Typography>
                  {normalizeBottomDarken(settings.bottomDarken).enabled && (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        {t('ecommerceSliderSettings.fields.bottomDarkenIntensity')}: {normalizeBottomDarken(settings.bottomDarken).intensity}%
                      </Typography>
                      <Slider
                        min={10}
                        max={80}
                        step={5}
                        value={normalizeBottomDarken(settings.bottomDarken).intensity}
                        disabled={!settings.enabled}
                        onChange={(_, v) =>
                          handleSettingChange('bottomDarken', {
                            ...normalizeBottomDarken(settings.bottomDarken),
                            intensity: v as number,
                          })
                        }
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}%`}
                        sx={{ mt: 0.5 }}
                      />
                    </>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, height: '100%', opacity: settings.enabled ? 1 : 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={normalizeImageBlur(settings.imageBlur).enabled}
                        disabled={!settings.enabled}
                        onChange={(e) =>
                          handleSettingChange('imageBlur', {
                            ...normalizeImageBlur(settings.imageBlur),
                            enabled: e.target.checked,
                          })
                        }
                      />
                    }
                    label={t('ecommerceSliderSettings.fields.imageBlur')}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {t('ecommerceSliderSettings.fields.imageBlurHint')}
                  </Typography>
                  {normalizeImageBlur(settings.imageBlur).enabled && (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        {t('ecommerceSliderSettings.fields.imageBlurAmount')}: {normalizeImageBlur(settings.imageBlur).amount}px
                      </Typography>
                      <Slider
                        min={2}
                        max={20}
                        step={1}
                        value={normalizeImageBlur(settings.imageBlur).amount}
                        disabled={!settings.enabled}
                        onChange={(_, v) =>
                          handleSettingChange('imageBlur', {
                            ...normalizeImageBlur(settings.imageBlur),
                            amount: v as number,
                          })
                        }
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}px`}
                        sx={{ mt: 0.5 }}
                      />
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Button 
              variant="outlined" 
              color="primary"
              disabled={!settings.enabled}
              onClick={handleOpenImageLibrary}
            >
              {t('ecommerceSliderSettings.actions.manageSliderImages')}
            </Button>
          </Grid>

          {(!settings.slides || settings.slides.length === 0) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Slider Arka Planı</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Slider'da hiç görsel olmadığında gösterilecek arka planı ayarlayın.
              </Typography>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={5}>
                    <Typography gutterBottom sx={{ fontWeight: 'medium' }}>Ayarlar</Typography>
                     <ToggleButtonGroup
                        color="primary"
                        value={backgroundSettings.type}
                        exclusive
                        onChange={(e, newType) => {
                          if (newType) {
                            handleSettingChange('background', { ...backgroundSettings, type: newType });
                          }
                        }}
                        aria-label="Arka Plan Tipi"
                        fullWidth
                        sx={{ mb: 2 }}
                      >
                        <ToggleButton value="solid">Düz Renk</ToggleButton>
                        <ToggleButton value="linear">Doğrusal Geçiş</ToggleButton>
                      </ToggleButtonGroup>

                      {backgroundSettings.type === 'solid' && (
                        <TextField
                          fullWidth
                          label="Arka Plan Rengi"
                          value={backgroundSettings.color}
                          onChange={(e) => handleSettingChange('background', { ...backgroundSettings, color: e.target.value })}
                          variant="outlined"
                          size="small"
                          InputProps={{
                              startAdornment: (
                              <InputAdornment position="start">
                                  <Box 
                                      component="input" 
                                      type="color" 
                                      value={backgroundSettings.color} 
                                      onChange={(e) => handleSettingChange('background', { ...backgroundSettings, color: e.target.value })} 
                                      sx={{ border: 'none', width: '24px', height: '24px', p: 0, background: 'none', cursor: 'pointer' }}
                                  />
                              </InputAdornment>
                              )
                          }}
                        />
                      )}

                      {backgroundSettings.type === 'linear' && (
                        <>
                          <Typography gutterBottom>Geçiş Yönü ({backgroundSettings.angle}°)</Typography>
                          <Slider
                            value={backgroundSettings.angle}
                            onChange={(e, newValue) => handleSettingChange('background', { ...backgroundSettings, angle: newValue as number })}
                            aria-labelledby="angle-slider"
                            valueLabelDisplay="auto"
                            step={1}
                            min={0}
                            max={360}
                          />
                          <Typography gutterBottom sx={{mt: 2}}>Renkler</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {(backgroundSettings.colors || []).map((color, index) => (
                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <input
                                    type="color"
                                    value={color}
                                    onChange={(e) => {
                                      const newColors = [...(backgroundSettings.colors || [])];
                                      newColors[index] = e.target.value;
                                      handleSettingChange('background', { ...backgroundSettings, colors: newColors });
                                    }}
                                    style={{ border: 'none', width: '24px', height: '24px', background: 'none', cursor: 'pointer', marginRight: '8px' }}
                                />
                                <Typography variant="body2" sx={{ mr: 1, fontFamily: 'monospace' }}>{color}</Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const newColors = (backgroundSettings.colors || []).filter((_, i) => i !== index);
                                    handleSettingChange('background', { ...backgroundSettings, colors: newColors });
                                  }}
                                  disabled={(backgroundSettings.colors || []).length <= 2}
                                >
                                  <DeleteIcon fontSize="inherit" />
                                </IconButton>
                              </Box>
                            ))}
                             <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                const newColors = [...(backgroundSettings.colors || []), '#ffffff'];
                                handleSettingChange('background', { ...backgroundSettings, colors: newColors });
                              }}
                            >
                              Renk Ekle
                            </Button>
                          </Box>
                        </>
                      )}
                  </Grid>
                  <Grid item xs={12} md={7}>
                      <Typography gutterBottom sx={{ fontWeight: 'medium' }}>Önizleme</Typography>
                      <Box
                          sx={{
                              width: '100%',
                              height: 180,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: generateCssBackground(backgroundSettings)
                          }}
                      >
                          <Typography sx={{ color: '#fff', textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}>
                              Slider Alanı
                          </Typography>
                      </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>{t('ecommerceSliderSettings.loadedImagesTitle')}</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{t('ecommerceSliderSettings.mobileCompatibilityTitle')}</strong> {t('ecommerceSliderSettings.mobileCompatibilityDescription')}
          </Typography>
        </Alert>
        
        {(!settings.slides || settings.slides.length === 0) ? (
            <Alert severity="info">{t('ecommerceSliderSettings.noSliderImages')}</Alert>
        ) : (
            <Grid container spacing={2}>
                {(settings.slides || []).map((slide: SlideItem) => (
                    <Grid item key={slide.id} xs={12} sm={6} md={4}>
                        <Card sx={{ position: 'relative' }}>
                            <CardMedia
                                component={slide.mediaType === 'video' ? 'video' : 'img'}
                                height="140"
                                {...(slide.mediaType === 'video'
                                  ? { src: slide.mediaUrl, controls: true, muted: true }
                                  : { image: slide.mediaUrl })}
                                title={slide.title}
                            />
                            <CardContent sx={{ pb: 1 }}>
                                <Typography gutterBottom variant="h6" component="div" noWrap>
                                    {slide.title}
                                </Typography>
                                
                                {/* Mobil Resim Yönetimi */}
                                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <PhoneAndroidIcon sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                      {t('ecommerceSliderSettings.mobileImageLabel')}
                                    </Typography>
                                  </Box>
                                  
                                  {slide.mobileMediaUrl ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box
                                        component="img"
                                        src={slide.mobileMediaUrl}
                                        alt={t('ecommerceSliderSettings.mobilePreviewAlt')}
                                        sx={{
                                          width: 40,
                                          height: 30,
                                          objectFit: 'cover',
                                          borderRadius: 0.5,
                                          border: '1px solid',
                                          borderColor: 'divider'
                                        }}
                                      />
                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveMobileImage(slide.id)}
                                        sx={{ minWidth: 'auto', p: 0.5 }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 14 }} />
                                      </Button>
                                    </Box>
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleOpenMobileImageLibrary(slide.id)}
                                      startIcon={<PhoneAndroidIcon sx={{ fontSize: 14 }} />}
                                      sx={{ 
                                        fontSize: '0.7rem', 
                                        py: 0.5, 
                                        px: 1,
                                        minHeight: 'auto'
                                      }}
                                    >
                                      {t('ecommerceSliderSettings.actions.addMobileImage')}
                                    </Button>
                                  )}
                                </Box>
                            </CardContent>
                            <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SmartButtonIcon />}
                                    onClick={() => handleOpenSlideSettingsModal(slide, 'button')}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {(() => {
                                      const active = getSlideButtons(slide).filter((b) => b.show && b.label);
                                      if (active.length === 0) return t('ecommerceSliderSettings.actions.addButton');
                                      return t('ecommerceSliderSettings.actions.manageButtons', {
                                        count: active.length,
                                      });
                                    })()}
                                  </Button>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <IconButton 
                                    onClick={() => handleOpenSlideSettingsModal(slide, 'general')} 
                                    size="small" 
                                    color="primary"
                                    title={t('ecommerceSliderSettings.actions.slideSettings')}
                                  >
                                      <SettingsIcon />
                                  </IconButton>
                                  <IconButton 
                                    onClick={() => handleInitiateDelete(slide)} 
                                    size="small" 
                                    color="error"
                                    disabled={isDeleting === slide.id}
                                  >
                                      <DeleteIcon />
                                  </IconButton>
                                </Box>
                            </CardActions>
                             {isDeleting === slide.id && (
                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.7)'}}>
                                    <CircularProgress />
                                </Box>
                            )}
                        </Card>
                    </Grid>
                ))}
            </Grid>
        )}

        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>{t('ecommerceSliderSettings.generalTextSettingsTitle')}</Typography>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 2, opacity: settings.enabled ? 1 : 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('ecommerceSliderSettings.textModeLabel')}
            </Typography>
            <Grid container spacing={1.25} sx={{ mb: 2.5 }}>
              {([
                { value: 'single' as SliderTextMode, titleKey: 'textModeSingleTitle', descKey: 'textModeSingleDesc' },
                { value: 'per-slide' as SliderTextMode, titleKey: 'textModePerSlideTitle', descKey: 'textModePerSlideDesc' },
                { value: 'fixed' as SliderTextMode, titleKey: 'textModeFixedTitle', descKey: 'textModeFixedDesc' },
              ]).map((mode) => {
                const selected = (settings.textMode || 'per-slide') === mode.value;
                return (
                  <Grid item xs={12} md={4} key={mode.value}>
                    <Box
                      onClick={() => {
                        if (!settings.enabled) return;
                        handleSettingChange('textMode', mode.value);
                        if (
                          mode.value === 'single' &&
                          !settings.textSlideId &&
                          settings.slides?.[0]?.id
                        ) {
                          handleSettingChange('textSlideId', settings.slides[0].id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      sx={{
                        p: 1.5,
                        height: '100%',
                        borderRadius: 1.5,
                        cursor: settings.enabled ? 'pointer' : 'default',
                        border: selected ? '2px solid' : '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'primary.50' : 'background.paper',
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        {t(`ecommerceSliderSettings.${mode.titleKey}`)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(`ecommerceSliderSettings.${mode.descKey}`)}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {(settings.textMode || 'per-slide') === 'single' && (
              <FormControl fullWidth size="small" disabled={!settings.enabled} sx={{ mb: 2.5 }}>
                <InputLabel>{t('ecommerceSliderSettings.textSlideLabel')}</InputLabel>
                <Select
                  label={t('ecommerceSliderSettings.textSlideLabel')}
                  value={settings.textSlideId || settings.slides?.[0]?.id || ''}
                  onChange={(e) => handleSettingChange('textSlideId', e.target.value)}
                >
                  {(settings.slides || []).map((slide, idx) => (
                    <MenuItem key={slide.id} value={slide.id}>
                      {slide.title?.trim()
                        ? slide.title
                        : t('ecommerceSliderSettings.textSlideOption', { index: idx + 1 })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {(settings.textMode || 'per-slide') === 'per-slide' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('ecommerceSliderSettings.textModePerSlideHint')}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid item xs={12} sm={6} md={5}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  {t('ecommerceSlideModal.fields.textPosition')}
                </Typography>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: '#334155',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    opacity: settings.enabled ? 1 : 0.5,
                  }}
                >
                  {positionGrid.map((row, rowIdx) => (
                    <Box key={`pos_row_${rowIdx}`} sx={{ display: 'flex', gap: 0.5 }}>
                      {row.map((cell) => {
                        const selected = (settings.textPosition || 'middle-center') === cell.value;
                        return (
                          <Button
                            key={cell.value}
                            size="small"
                            disabled={!settings.enabled}
                            onClick={() => handleSettingChange('textPosition', cell.value)}
                            variant={selected ? 'contained' : 'outlined'}
                            title={positionOptions[cell.value as keyof typeof positionOptions] || cell.value}
                            sx={{
                              minWidth: 40,
                              width: 40,
                              height: 36,
                              p: 0,
                              fontSize: '1rem',
                              borderColor: selected ? 'primary.main' : 'rgba(255,255,255,0.35)',
                              color: selected ? undefined : '#fff',
                            }}
                          >
                            {cell.label}
                          </Button>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {positionOptions[(settings.textPosition || 'middle-center') as keyof typeof positionOptions]}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.75 }}>
                  {t('ecommerceSlideModal.fields.textAlign')}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  disabled={!settings.enabled}
                  value={resolveTextAlign(settings.textAlign, settings.textPosition)}
                  onChange={(_e, value: SliderTextAlign | null) => {
                    if (value) handleSettingChange('textAlign', value);
                  }}
                >
                  <ToggleButton value="left" aria-label={t('ecommerceSlideModal.fields.alignLeft')}>
                    <FormatAlignLeftIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="center" aria-label={t('ecommerceSlideModal.fields.alignCenter')}>
                    <FormatAlignCenterIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="right" aria-label={t('ecommerceSlideModal.fields.alignRight')}>
                    <FormatAlignRightIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {t('ecommerceSlideModal.fields.textAlignHint')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.75 }}>
                  {t('ecommerceSliderSettings.fields.textInset')}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  disabled={!settings.enabled}
                  value={normalizeTextInset(settings.textInset).mode}
                  onChange={(_e, value: SliderTextInsetMode | null) => {
                    if (value) {
                      handleSettingChange('textInset', {
                        ...normalizeTextInset(settings.textInset),
                        mode: value,
                      });
                    }
                  }}
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="grid">{t('ecommerceSliderSettings.fields.textInsetGrid')}</ToggleButton>
                  <ToggleButton value="edge">{t('ecommerceSliderSettings.fields.textInsetEdge')}</ToggleButton>
                  <ToggleButton value="custom">{t('ecommerceSliderSettings.fields.textInsetCustom')}</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {t('ecommerceSliderSettings.fields.textInsetHint')}
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('ecommerceSliderSettings.fields.textInsetOffset')}: {normalizeTextInset(settings.textInset).offsetX || 0}px
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 28 }}>
                      {t('ecommerceSliderSettings.fields.textInsetLeft')}
                    </Typography>
                    <Slider
                      min={-160}
                      max={160}
                      step={4}
                      value={normalizeTextInset(settings.textInset).offsetX || 0}
                      disabled={!settings.enabled}
                      onChange={(_, v) =>
                        handleSettingChange('textInset', {
                          ...normalizeTextInset(settings.textInset),
                          offsetX: v as number,
                        })
                      }
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v > 0 ? '+' : ''}${v}px`}
                      marks={[
                        { value: -160, label: '' },
                        { value: 0, label: '' },
                        { value: 160, label: '' },
                      ]}
                      sx={{ mt: 0.5, flex: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 28, textAlign: 'right' }}>
                      {t('ecommerceSliderSettings.fields.textInsetRight')}
                    </Typography>
                  </Box>
                </Box>
                {normalizeTextInset(settings.textInset).mode === 'custom' && (
                  <Box sx={{ mt: 1.25 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('ecommerceSliderSettings.fields.textInsetCustomPx')}: {normalizeTextInset(settings.textInset).customPx}px
                    </Typography>
                    <Slider
                      min={16}
                      max={160}
                      step={4}
                      value={normalizeTextInset(settings.textInset).customPx || 48}
                      disabled={!settings.enabled}
                      onChange={(_, v) =>
                        handleSettingChange('textInset', {
                          ...normalizeTextInset(settings.textInset),
                          customPx: v as number,
                        })
                      }
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v}px`}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={7}>
                <FormControl fullWidth size="small" disabled={!settings.enabled} sx={{ mb: 2 }}>
                  <InputLabel>{t('ecommerceSlideModal.fields.entryAnimation')}</InputLabel>
                  <Select
                    label={t('ecommerceSlideModal.fields.entryAnimation')}
                    value={settings.textAnimation || 'fade-in'}
                    onChange={(e) => handleSettingChange('textAnimation', e.target.value)}
                  >
                    {Object.entries(animationOptions).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    minHeight: 96,
                    bgcolor: '#334155',
                    color: '#fff',
                    display: 'flex',
                    alignItems:
                      (settings.textPosition || 'middle-center').includes('top')
                        ? 'flex-start'
                        : (settings.textPosition || 'middle-center').includes('bottom')
                          ? 'flex-end'
                          : 'center',
                    justifyContent:
                      (settings.textPosition || 'middle-center').includes('left')
                        ? 'flex-start'
                        : (settings.textPosition || 'middle-center').includes('right')
                          ? 'flex-end'
                          : 'center',
                    textAlign: resolveTextAlign(settings.textAlign, settings.textPosition),
                    px: normalizeTextInset(settings.textInset).mode === 'edge'
                      ? 1
                      : normalizeTextInset(settings.textInset).mode === 'custom'
                        ? `${Math.round((normalizeTextInset(settings.textInset).customPx || 48) * 0.25)}px`
                        : 3,
                  }}
                >
                  <Box
                    sx={{
                      width: 'max-content',
                      maxWidth: '90%',
                      textAlign: 'inherit',
                      transform: `translateX(${Math.round((normalizeTextInset(settings.textInset).offsetX || 0) * 0.35)}px)`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: titleSettings.color,
                        fontSize: Math.min(28, Math.max(12, parseInt(String(titleSettings.fontSize).replace(/\D/g, ''), 10) || 24) * 0.45),
                        fontFamily: titleSettings.fontFamily,
                        fontWeight: titleSettings.fontWeight,
                        fontStyle: titleSettings.fontStyle,
                        lineHeight: 1.15,
                        textAlign: 'inherit',
                      }}
                    >
                      {renderAccentSplitTitle(
                        titleSettings.text || t('ecommerceSlideModal.fields.titleText'),
                        {
                          mode: titleSettings.titleColorMode,
                          highlightText: titleSettings.highlightText,
                          accentColor: titleSettings.highlightTextColor || '#2563eb',
                          highlightStyle: titleSettings.highlightFontSize
                            ? {
                                fontSize: Math.min(
                                  32,
                                  Math.max(
                                    12,
                                    (parseInt(String(titleSettings.highlightFontSize).replace(/\D/g, ''), 10) || 24) * 0.45
                                  )
                                ),
                              }
                            : undefined,
                        }
                      )}
                    </Typography>
                    {descriptionSettings.text ? (
                      <Typography
                        sx={{
                          mt: 0.5,
                          color: descriptionSettings.color,
                          fontSize: Math.min(16, Math.max(10, parseInt(String(descriptionSettings.fontSize).replace(/\D/g, ''), 10) || 14) * 0.55),
                          fontFamily: descriptionSettings.fontFamily,
                          fontWeight: descriptionSettings.fontWeight,
                          fontStyle: descriptionSettings.fontStyle,
                          textAlign: 'inherit',
                        }}
                      >
                        {descriptionSettings.text}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </Grid>
            </Grid>
            
            <Typography gutterBottom sx={{ fontWeight: 'medium' }}>{t('ecommerceSliderSettings.titleSettings')}</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={7} md={8}>
                        <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        label={t('ecommerceSlideModal.fields.titleText')}
                        value={titleSettings.text}
                        onChange={(e) => handleSettingChange('title', { ...titleSettings, text: e.target.value })}
                        variant="outlined"
                        size="small"
                        disabled={!settings.enabled}
                        helperText={t('ecommerceSliderSettings.titleLineBreakHint')}
                    />
                </Grid>
                <Grid item xs={12} sm={5} md={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('ecommerceSliderSettings.fields.titleFontSize')}: {parseInt(String(titleSettings.fontSize), 10) || 48}px
                    </Typography>
                    <Slider
                      min={16}
                      max={96}
                      step={2}
                      value={parseInt(String(titleSettings.fontSize), 10) || 48}
                      disabled={!settings.enabled}
                      onChange={(_, v) =>
                        handleSettingChange('title', {
                          ...titleSettings,
                          fontSize: `${v as number}px`,
                        })
                      }
                      valueLabelDisplay="auto"
                      sx={{ mt: 0.5, mx: 0.5 }}
                    />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={titleSettings.titleColorMode === 'accent-split'}
                        disabled={!settings.enabled}
                        onChange={(e) =>
                          handleSettingChange('title', {
                            ...titleSettings,
                            titleColorMode: e.target.checked ? 'accent-split' : 'solid',
                          })
                        }
                      />
                    }
                    label={t('ecommerceSliderSettings.accentSplitTitle')}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 0.5, mb: titleSettings.titleColorMode === 'accent-split' ? 1 : 0 }}>
                    {t('ecommerceSliderSettings.accentSplitTitleHint')}
                  </Typography>
                </Grid>
                {titleSettings.titleColorMode === 'accent-split' && (
                  <>
                    <Grid item xs={12} sm={7} md={8}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('ecommerceSliderSettings.highlightText')}
                        value={titleSettings.highlightText || ''}
                        disabled={!settings.enabled}
                        onChange={(e) =>
                          handleSettingChange('title', {
                            ...titleSettings,
                            highlightText: e.target.value,
                          })
                        }
                        helperText={t('ecommerceSliderSettings.highlightTextHelper')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5} md={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('ecommerceSliderSettings.fields.highlightFontSize')}: {parseInt(String(titleSettings.highlightFontSize || titleSettings.fontSize), 10) || 48}px
                      </Typography>
                      <Slider
                        min={16}
                        max={96}
                        step={2}
                        value={parseInt(String(titleSettings.highlightFontSize || titleSettings.fontSize), 10) || 48}
                        disabled={!settings.enabled}
                        onChange={(_, v) =>
                          handleSettingChange('title', {
                            ...titleSettings,
                            highlightFontSize: `${v as number}px`,
                          })
                        }
                        valueLabelDisplay="auto"
                        sx={{ mt: 0.5, mx: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextColorPalette
                        label={t('ecommerceSliderSettings.highlightTextColor')}
                        value={titleSettings.highlightTextColor || ''}
                        disabled={!settings.enabled}
                        onChange={(color) =>
                          handleSettingChange('title', {
                            ...titleSettings,
                            highlightTextColor: color,
                          })
                        }
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={4}>
                    <TextColorPalette
                        label={t('ecommerceSlideModal.fields.textColor')}
                        value={titleSettings.color}
                        disabled={!settings.enabled}
                        onChange={(color) => handleSettingChange('title', { ...titleSettings, color })}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small" disabled={!settings.enabled}>
                    <InputLabel>{t('ecommerceSlideModal.fields.fontFamily')}</InputLabel>
                    <Select
                        label={t('ecommerceSlideModal.fields.fontFamily')}
                        value={titleSettings.fontFamily}
                        onChange={(e) => handleSettingChange('title', { ...titleSettings, fontFamily: e.target.value })}
                    >
                        {fontOptions.map(font => <MenuItem key={font} value={font}>{font.split(',')[0].replace(/'/g, '')}</MenuItem>)}
                    </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                <ToggleButtonGroup
                    value={[
                        titleSettings.fontWeight === 'bold' ? 'bold' : '',
                        titleSettings.fontStyle === 'italic' ? 'italic' : ''
                    ]}
                    onChange={(e, newFormats) => {
                        handleSettingChange('title', {
                            ...titleSettings,
                            fontWeight: newFormats.includes('bold') ? 'bold' : 'normal',
                            fontStyle: newFormats.includes('italic') ? 'italic' : 'normal',
                        });
                    }}
                    disabled={!settings.enabled}
                    aria-label="text formatting"
                    size="small"
                >
                    <ToggleButton value="bold" aria-label="bold">
                        <FormatBoldIcon />
                    </ToggleButton>
                    <ToggleButton value="italic" aria-label="italic">
                        <FormatItalicIcon />
                    </ToggleButton>
                </ToggleButtonGroup>
                </Grid>
            </Grid>
        </Box>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 2, opacity: settings.enabled ? 1 : 0.5 }}>
            <Typography gutterBottom sx={{ fontWeight: 'medium' }}>{t('ecommerceSliderSettings.descriptionSettings')}</Typography>
            <Grid container spacing={2}>
            <Grid item xs={12} sm={7} md={8}>
                <TextField
                    fullWidth
                    label={t('ecommerceSlideModal.fields.descriptionText')}
                    value={descriptionSettings.text}
                    onChange={(e) => handleSettingChange('description', { ...descriptionSettings, text: e.target.value })}
                    variant="outlined"
                    size="small"
                    multiline
                    rows={2}
                    disabled={!settings.enabled}
                />
            </Grid>
            <Grid item xs={12} sm={5} md={4} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <TextField
                    fullWidth
                    label={t('ecommerceSlideModal.fields.fontSize')}
                    type="number"
                    value={parseInt(descriptionSettings.fontSize) || ''}
                    onChange={(e) => handleSettingChange('description', { ...descriptionSettings, fontSize: `${e.target.value}px` })}
                    variant="outlined"
                    size="small"
                    disabled={!settings.enabled}
                    InputProps={{
                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                    }}
                    helperText={t('ecommerceSliderSettings.fields.pxHelper')}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextColorPalette
                    label={t('ecommerceSlideModal.fields.textColor')}
                    value={descriptionSettings.color}
                    disabled={!settings.enabled}
                    onChange={(color) => handleSettingChange('description', { ...descriptionSettings, color })}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" disabled={!settings.enabled}>
                <InputLabel>{t('ecommerceSlideModal.fields.fontFamily')}</InputLabel>
                <Select
                    label={t('ecommerceSlideModal.fields.fontFamily')}
                    value={descriptionSettings.fontFamily}
                    onChange={(e) => handleSettingChange('description', { ...descriptionSettings, fontFamily: e.target.value })}
                >
                    {fontOptions.map(font => <MenuItem key={font} value={font}>{font.split(',')[0].replace(/'/g, '')}</MenuItem>)}
                </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <ToggleButtonGroup
                    value={[
                        descriptionSettings.fontWeight === 'bold' ? 'bold' : '',
                        descriptionSettings.fontStyle === 'italic' ? 'italic' : ''
                    ]}
                    onChange={(e, newFormats) => {
                        handleSettingChange('description', {
                            ...descriptionSettings,
                            fontWeight: newFormats.includes('bold') ? 'bold' : 'normal',
                            fontStyle: newFormats.includes('italic') ? 'italic' : 'normal',
                        });
                    }}
                    disabled={!settings.enabled}
                    aria-label="text formatting"
                    size="small"
                >
                    <ToggleButton value="bold" aria-label="bold">
                        <FormatBoldIcon />
                    </ToggleButton>
                    <ToggleButton value="italic" aria-label="italic">
                        <FormatItalicIcon />
                    </ToggleButton>
                </ToggleButtonGroup>
            </Grid>
            </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />
        <TrustStripSettingsPanel
          compact
          enabled={Boolean(settings.showTrustStrip)}
          settings={normalizeTrustStripSettings(settings.trustStripSettings || DEFAULT_TRUST_STRIP)}
          onEnabledChange={(enabled) => {
            setTrustStripSaved(false);
            void handleSettingChange('showTrustStrip', enabled);
          }}
          onSettingsChange={(next) => {
            setTrustStripSaved(false);
            void handleSettingChange('trustStripSettings', normalizeTrustStripSettings(next));
          }}
          saving={trustStripSaving}
          saved={trustStripSaved}
          error={trustStripError}
          onSave={async () => {
            if (!companyId || !configId) {
              setTrustStripError(t('ecommerceHomeSettings.trustStrip.saveError'));
              return;
            }
            setTrustStripSaving(true);
            setTrustStripSaved(false);
            setTrustStripError(null);
            try {
              const trustSettings = normalizeTrustStripSettings(
                settings.trustStripSettings || DEFAULT_TRUST_STRIP
              );
              await saveSliderSettings({
                configId,
                companyId,
                showSlider: settings.enabled,
                height:
                  typeof settings.height === 'object'
                    ? settings.height.web || 60
                    : settings.height || 60,
                mobileHeight:
                  typeof settings.height === 'object'
                    ? settings.height.mobile || 40
                    : 40,
                autoPlay: settings.autoplay,
                autoPlayInterval: settings.interval,
                titleSettingsJson: settings.title ? JSON.stringify(settings.title) : null,
                descriptionSettingsJson: settings.description
                  ? JSON.stringify(settings.description)
                  : null,
                backgroundSettingsJson: JSON.stringify({
                  ...(settings.background || { type: 'solid', color: '#2e3141' }),
                  textOverlayMode: settings.textMode || 'per-slide',
                  textOverlaySlideId: settings.textSlideId || null,
                  textAlign: resolveTextAlign(settings.textAlign, settings.textPosition),
                  textPosition: settings.textPosition || 'middle-center',
                  bottomDarken: normalizeBottomDarken(settings.bottomDarken),
                  imageBlur: normalizeImageBlur(settings.imageBlur),
                  textInset: normalizeTextInset(settings.textInset),
                  heightWeb:
                    typeof settings.height === 'object'
                      ? settings.height.web || 60
                      : settings.height || 60,
                  heightMobile:
                    typeof settings.height === 'object'
                      ? settings.height.mobile || 40
                      : 40,
                  trustStrip: {
                    enabled: Boolean(settings.showTrustStrip),
                    ...trustSettings,
                  },
                }),
                textPosition: settings.textPosition || 'middle-center',
                textAnimation: settings.textAnimation || 'fade-in',
              });
              setTrustStripSaved(true);
              notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
            } catch (err) {
              console.error('❌ [TRUST STRIP] Slider JSON kayıt hatası:', err);
              setTrustStripError(t('ecommerceHomeSettings.trustStrip.saveError'));
            } finally {
              setTrustStripSaving(false);
            }
          }}
        />
      </Paper>
      <ImageLibrarySelectionModal
        open={isImageLibraryOpen}
        onClose={handleCloseImageLibrary}
        onSave={handleImageLibrarySave}
        companyId={companyId}
        productName={t('ecommerceSliderSettings.imageLibrary.sliderImageName')}
        currentImageUrls={[]}
        productId={null}
      />
      <ImageLibrarySelectionModal
        open={isMobileImageLibraryOpen}
        onClose={() => { setIsMobileImageLibraryOpen(false); setMobileImageTargetSlideId(null); }}
        onSave={handleMobileImageLibrarySave}
        companyId={companyId}
        productName={t('ecommerceSliderSettings.imageLibrary.mobileSliderImageName')}
        currentImageUrls={[]}
        productId={null}
        singleSelect
      />
      <SlideSpecificSettingsModal
        open={!!editingSlide}
        onClose={handleCloseSlideSettingsModal}
        slide={editingSlide}
        onSave={handleSaveSlideSettings}
        initialFocus={slideModalFocus}
        configId={configId}
      />
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Slaytı Silmeyi Onayla"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            "{slideToDelete?.title}" başlıklı slaytı kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>İptal</Button>
          <Button onClick={handleDeleteSlide} color="error" autoFocus>
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SliderSettingsComponent;

